import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { has, pickBy } from 'lodash';
import qs from 'qs';

import TableHeaderCheckboxAll from '../../../../../components/Table/CheckAll';
import DropdownRequest from '../../../../../components/Dropdown/Request';
import { generateTableHead, generateAccessMassDrop, getModesFilterName, getSelectedServices, getServicesFilterName } from '../../helpers';
import { MainContext } from '../../index';
import {
  getModesFilter,
  getModesFilterError,
  getModesFilterSuccess,
  getServiceFilter,
  getServiceFilterError,
  getServiceFilterSuccess,
} from '../../reducer';
import { refillGetModes, refillGetServices } from '../../../../../services/endpoints/refill';

const TableHead = ({ values, onChangeCheck, handleSelect, access, tasks, config }) => {
  const location = useLocation();
  const { darkMode } = useSelector((state) => state.general);
  const { t } = useTranslation();
  const history = useHistory();

  const queryString = qs.parse(location.search.slice(1));
  const [store, dispatch] = useContext(MainContext);
  const { services, modes, filters } = store;

  const accessMassActions = generateAccessMassDrop(access, t, queryString, config?.statuses, tasks);

  const selectedServices = useMemo(() => getSelectedServices(services.data, filters.service), [services.data, filters.service]);
  const servicesFilterName = useMemo(() => getServicesFilterName(filters.service), [filters.service]);
  const servicesFilterIsActive = !!filters.service;

  const modesFilterName = useMemo(() => getModesFilterName(modes.data, filters.mode), [filters.mode, modes.data]);
  const modesFilterIsActive = filters.mode !== null;

  const onClearFilters = () => true;
  const onRequestFiltersServices = async () => {
    const queryString = qs.parse(location.search.slice(1));
    dispatch(getServiceFilter());
    try {
      const response = await refillGetServices(queryString);
      if (response.data?.services) {
        const servicesList = response.data.services.map((service) => ({
          label: service.title,
          id: service.id,
          count: service.count,
        }));
        dispatch(getServiceFilterSuccess(servicesList));
      }
    } catch (error) {
      dispatch(getServiceFilterError());
    }
  };
  const onChangeSelectServices = (options) => {
    const newServices = options.filter((option) => option.id !== null).map((opt) => opt.id);
    const queryString = qs.parse(location.search.slice(1));
    delete queryString.service;
    const queryObj = {
      service: newServices,
      ...queryString,
    };
    if (has(queryObj, 'page')) delete queryObj.page;
    history.push({ pathname: '/refill', search: qs.stringify({ ...pickBy(queryObj) }) });
  };
  const onRequestFiltersModes = async () => {
    dispatch(getModesFilter());
    try {
      const response = await refillGetModes(queryString);
      if (response.data?.modes) {
        const delaysList = response.data.modes.map((delay) => ({
          title: `${delay.title} (${delay.count})`,
          id: delay.id,
        }));
        dispatch(getModesFilterSuccess(delaysList));
      }
    } catch (error) {
      dispatch(getModesFilterError());
    }
  };

  return (
    <thead>
      <tr>
        {generateTableHead().map((name, index) => {
          switch (name) {
          case 'All':
            if (accessMassActions.length === 0) {
              return null;
            }

            return (
              <TableHeaderCheckboxAll
                key={index}
                handleSelect={(key, title) => handleSelect({ key, title }, 'mass')}
                transKey="Table.MassCounterRefill"
                transValue="{{count}} tasks selected"
                values={values}
                onChangeCheck={onChangeCheck}
                accessMassActions={accessMassActions.defaulMassActions}
                submenuStatuses={accessMassActions.massDropStatuses}
                configAccess={{
                  see_order_id: true,
                  see_external_id: false,
                  see_link: false,
                  see_all_data: false,
                }}
              />
            );

          case 'Service':
            return (
              <th key={index} className="tickets__status">
                <DropdownRequest
                  title={servicesFilterName}
                  type="select"
                  items={services.data}
                  queryRequest="service"
                  onRequestFunc={onRequestFiltersServices}
                  onChangeSelect={onChangeSelectServices}
                  clearData={onClearFilters}
                  fetchItems={services.isLoading}
                  active={servicesFilterIsActive}
                  value={selectedServices}
                  darkMode={darkMode}
                  pullRight
                  isSorted
                />
              </th>
            );

          case 'Mode':
            return (
              <th key={index} className="tickets__status">
                <DropdownRequest
                  title={modesFilterName}
                  items={modes.data}
                  queryRequest="mode"
                  onRequestFunc={onRequestFiltersModes}
                  clearData={onClearFilters}
                  fetchItems={modes.isLoading}
                  active={modesFilterIsActive}
                  darkMode={darkMode}
                  pullRight
                />
              </th>
            );

          default:
            return <th key={index}>{name ? t(`Table.${name}`) : ''}</th>;
          }
        })}
      </tr>
    </thead>
  );
};

TableHead.propTypes = {
  tasks: PropTypes.array,
  values: PropTypes.array,
  onChangeCheck: PropTypes.func,
  handleSelect: PropTypes.func,
  access: PropTypes.object,
  config: PropTypes.object,
};

export default TableHead;
